/**
 * Form controls (controlled components) shared by the app pages and the editor.
 */
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

export function Field({
  label,
  children,
  hint,
  error,
  htmlFor,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
  error?: string | null;
  htmlFor?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-ink">
        {label}
      </label>
      {children}
      {error ? (
        <span className="text-xs text-danger">{error}</span>
      ) : (
        hint && <span className="text-xs text-mute">{hint}</span>
      )}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className = '', ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={`h-10 w-full rounded-lg border border-line bg-canvas px-3 text-sm text-ink shadow-sm placeholder:text-mute/70 focus:border-ink focus:outline-none disabled:bg-subtle disabled:text-mute ${className}`}
      {...rest}
    />
  );
});

export function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format = (v) => String(v),
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between text-sm">
        <label htmlFor={id} className="text-body">
          {label}
        </label>
        <span className="tabular text-ink">{format(value)}</span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full"
      />
    </div>
  );
}

export function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-line bg-canvas px-2.5 py-2 hover:border-line-strong"
    >
      <span
        className="relative size-6 shrink-0 overflow-hidden rounded-full border border-black/10"
        style={{ background: value }}
      >
        <input
          id={id}
          type="color"
          value={value.toLowerCase()}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="absolute inset-0 cursor-pointer opacity-0"
        />
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="text-sm text-ink">{label}</span>
        <span className="tabular text-xs text-mute">{value}</span>
      </span>
    </label>
  );
}

export function Toggle({
  label,
  checked,
  onChange,
  disabled,
  hint,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  hint?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between gap-4 py-1 text-left text-sm disabled:cursor-not-allowed"
    >
      <span className="flex flex-col">
        <span className={disabled ? 'text-mute' : 'text-ink'}>{label}</span>
        {hint && <span className="text-xs text-mute">{hint}</span>}
      </span>
      <span
        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
          checked ? 'bg-ink' : 'bg-line-strong'
        } ${disabled ? 'opacity-50' : ''}`}
      >
        <span
          className={`absolute top-1 left-1 size-4 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </span>
    </button>
  );
}

export function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  label,
}: {
  options: readonly { value: T; label: ReactNode; title?: string }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex gap-1.5">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          title={o.title}
          onClick={() => onChange(o.value)}
          className={`flex h-9 flex-1 items-center justify-center rounded-lg border px-2 text-sm font-medium transition-colors ${
            o.value === value
              ? 'border-ink bg-ink text-white'
              : 'border-line bg-canvas text-body hover:border-line-strong'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Select<T extends string | number>({
  value,
  options,
  onChange,
  label,
  className = '',
}: {
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (v: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <div className={`relative ${className}`}>
      <select
        aria-label={label}
        value={String(value)}
        onChange={(e) => {
          const found = options.find((o) => String(o.value) === e.target.value);
          if (found) onChange(found.value);
        }}
        className="h-10 w-full appearance-none rounded-lg border border-line bg-canvas pr-9 pl-3 text-sm text-ink shadow-sm focus:border-ink focus:outline-none"
      >
        {options.map((o) => (
          <option key={String(o.value)} value={String(o.value)}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={16}
        className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-mute"
      />
    </div>
  );
}

export function ProgressBar({
  value,
  label,
  className = '',
}: {
  value: number;
  label?: ReactNode;
  className?: string;
}) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div
      className={`flex flex-col gap-1.5 ${className}`}
      role="progressbar"
      aria-valuenow={Math.round(v)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {label && (
        <div className="flex justify-between text-sm">
          <span className="text-body">{label}</span>
          <span className="tabular text-ink">{Math.round(v)}%</span>
        </div>
      )}
      <div className="h-2 overflow-hidden rounded-full bg-soft">
        <div
          className="h-full rounded-full bg-ink transition-[width] duration-300"
          style={{ width: `${v}%` }}
        />
      </div>
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-xl border border-line bg-canvas shadow-sm ${className}`}>{children}</div>;
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'ok' | 'warn' | 'danger' | 'dark';
}) {
  const tones = {
    neutral: 'bg-soft text-body',
    ok: 'bg-ok/10 text-ok',
    warn: 'bg-warn/10 text-warn',
    danger: 'bg-danger/10 text-danger',
    dark: 'bg-ink text-white',
  } as const;
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}
